import { useState } from 'react'

import { Icon } from '@/common/components/Icon'
import Loader from '@/common/components/Loader'
import { Badge, Button } from '@/components/ui'
import { IColumn, Table } from '@/core/components/Table'
import { sanitizeDate } from '@/core/constants/times'
import {
  cancelCoinVoucherBatch,
  CoinVouchersBatchDownloadListResponse,
} from '@/core/services/voucher'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'

export interface RedeemVouchersDownloadsTableProps {
  data: CoinVouchersBatchDownloadListResponse[]
  sortBy: string
  setSortBy: (sort: string) => void
  onUpdated: () => void
}

export const RedeemVouchersDownloadsTable = (
  props: RedeemVouchersDownloadsTableProps,
): JSX.Element => {
  const [loading, setLoading] = useState<boolean>(false)

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { sortBy, setSortBy, onUpdated } = props

  const [showDeleteConfirmationModal, setShowDeleteConfirmationModal] =
    useState<boolean>(false)
  const [batch, setBatch] =
    useState<CoinVouchersBatchDownloadListResponse | null>(null)

  const handleDeleteBatch = async () => {
    if (!batch) return
    setLoading(true)
    try {
      const { status } = await cancelCoinVoucherBatch(batch?.id)
      if (status === 200 || status === 204) {
        setShowDeleteConfirmationModal(false)
        setBatch(null)
        onUpdated()
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
        setErrorStatus(
          (e as { response?: { status?: number } })?.response?.status,
        )
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSortBy = (sort: string) => {
    if (sort === sortBy) {
      setSortBy('id')
    } else {
      setSortBy(sort)
    }
  }

  const columns: IColumn[] = [
    {
      title: 'Name',
      accessor: 'name',
      sortable: true,
      render: (row: CoinVouchersBatchDownloadListResponse) => (
        <div className="flex flex-col">
          <span>{row.label}</span>
          <div className="flex gap-2">
            <a
              href={row.zipLink}
              className="underline text-accent-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              zip
            </a>
            <a
              href={row.zipLink}
              className="underline text-accent-1"
              target="_blank"
              rel="noopener noreferrer"
            >
              pdf
            </a>
          </div>
        </div>
      ),
    },
    {
      title: 'Generated Date',
      accessor: 'generatedDate',
      render: (row: CoinVouchersBatchDownloadListResponse) =>
        sanitizeDate(new Date(row.createdAt)),
    },
    {
      title: 'ZIP Password',
      accessor: 'zipPassword',
      render: (row: CoinVouchersBatchDownloadListResponse) => row.password,
    },
    {
      title: 'Status',
      accessor: 'status',
      render: (row: CoinVouchersBatchDownloadListResponse) => (
        <Badge
          variant={
            row.status === 'COMPLETED'
              ? 'success'
              : row.status === 'PROCESSING'
              ? 'warning'
              : 'danger'
          }
          className="h-[36px]"
        >
          {row.status}
        </Badge>
      ),
    },
    {
      title: 'Value',
      accessor: 'value',
      render: (row: CoinVouchersBatchDownloadListResponse) =>
        `${row.amount} RHUNA`,
    },
    {
      title: 'Action',
      accessor: 'action',
      render: (row: CoinVouchersBatchDownloadListResponse) =>
        row.status !== 'CANCELED' ? (
          <Button
            variant="secondary"
            className="border border-monochrome-120"
            onClick={() => {
              setBatch(row)
              setShowDeleteConfirmationModal(true)
            }}
          >
            cancel order
            <Icon
              icon="close-circle"
              width="16"
              height="16"
              viewBox="0 0 16 16"
            />
          </Button>
        ) : (
          '-'
        ),
    },
  ]

  return (
    <>
      {loading && <Loader />}
      <Table
        className="[&_th]:!whitespace-nowrap"
        columns={columns}
        data={props.data}
        handleSortBy={handleSortBy}
      />

      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setShowDeleteConfirmationModal}
        deleteTitle="Batch"
        deleteFunc={handleDeleteBatch}
        deletedItem={batch?.label}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
